import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './ProjectDetails.css';

const API_BASE_URL = 'http://72.61.235.16:5001/api/v1';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [worklogs, setWorklogs] = useState([]);
  
  // Modal states
  const [showPMModal, setShowPMModal] = useState(false);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [showFinanceModal, setShowFinanceModal] = useState(false);
  const [projectManagers, setProjectManagers] = useState([]);
  const [siteIncharges, setSiteIncharges] = useState([]);
  const [financeUsers, setFinanceUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProjectDetails();
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch project
      const projectResponse = await axios.get(`${API_BASE_URL}/projects/${id}`, config);
      const projectData = projectResponse.data.data.project;
      setProject(projectData);

      // Fetch payment summary if applicable
      if (['CUSTOMER_APPROVED', 'WORK_STARTED', 'COMPLETED', 'FINAL_PAID', 'CLOSED'].includes(projectData.status)) {
        await fetchPaymentSummary(projectData.id);
      }

      // Fetch worklogs if work started
      if (['WORK_STARTED', 'COMPLETED', 'CLOSED'].includes(projectData.status)) {
        await fetchWorklogs(projectData.id);
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      alert('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentSummary = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [quotationRes, paymentsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/quotations/project/${projectId}`, config),
        axios.get(`${API_BASE_URL}/payments/project/${projectId}`, config)
      ]);

      const quotation = quotationRes.data.data.quotation;
      const payments = paymentsRes.data.data.payments;

      const totalPaid = payments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + parseFloat(p.amount), 0);

      const extraCharges = payments.filter(p => p.type === 'extra');
      const extraChargesTotal = extraCharges.reduce((sum, p) => sum + parseFloat(p.amount), 0);

      const totalCost = parseFloat(quotation.total_cost);
      const totalCostWithExtras = totalCost + extraChargesTotal;
      const balance = totalCostWithExtras - totalPaid;

      setPaymentSummary({
        totalCost,
        extraChargesTotal,
        totalCostWithExtras,
        totalPaid,
        balance,
        payments,
      });
    } catch (error) {
      console.error('Error fetching payment summary:', error);
    }
  };

  const fetchWorklogs = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/worklogs/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorklogs(response.data.data.worklogs || []);
    } catch (error) {
      console.error('Error fetching worklogs:', error);
    }
  };

  const fetchProjectManagers = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pmUsers = response.data.data.users.filter(
        u => u.role === 'project_manager' && u.is_active
      );
      setProjectManagers(pmUsers);
    } catch (error) {
      console.error('Error fetching PMs:', error);
      alert('Failed to load project managers');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchSiteIncharges = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const siteUsers = response.data.data.users.filter(
        u => u.role === 'site_incharge' && u.is_active
      );
      setSiteIncharges(siteUsers);
    } catch (error) {
      console.error('Error fetching site incharges:', error);
      alert('Failed to load site incharges');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchFinanceUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const financeList = response.data.data.users.filter(
        u => u.role === 'finance' && u.is_active
      );
      setFinanceUsers(financeList);
    } catch (error) {
      console.error('Error fetching finance users:', error);
      alert('Failed to load finance users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssignPM = async (pmId) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}/projects/${id}/assign-pm`,
        { pmId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(response.data.data.project);
      setShowPMModal(false);
      alert('Project Manager assigned successfully!');
      fetchProjectDetails();
    } catch (error) {
      console.error('Error assigning PM:', error);
      alert(error.response?.data?.message || 'Failed to assign Project Manager');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignSite = async (siteId) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}/projects/${id}/assign-site`,
        { siteId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(response.data.data.project);
      setShowSiteModal(false);
      alert('Site Incharge assigned successfully!');
      fetchProjectDetails();
    } catch (error) {
      console.error('Error assigning site:', error);
      alert(error.response?.data?.message || 'Failed to assign Site Incharge');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignFinance = async (financeId) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}/projects/${id}/assign-finance`,
        { financeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(response.data.data.project);
      setShowFinanceModal(false);
      alert('Finance Manager assigned successfully!');
      fetchProjectDetails();
    } catch (error) {
      console.error('Error assigning finance:', error);
      alert(error.response?.data?.message || 'Failed to assign Finance Manager');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (newStatus, confirmMessage) => {
    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}/projects/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(response.data.data.project);
      alert('Status updated successfully!');
      fetchProjectDetails();
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error.response?.data?.message || 'Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      CREATED: '#94a3b8',
      PM_ASSIGNED: '#3b82f6',
      VISIT_DONE: '#06b6d4',
      REPORT_SUBMITTED: '#8b5cf6',
      QUOTATION_GENERATED: '#f59e0b',
      CUSTOMER_APPROVED: '#10b981',
      ADVANCE_PENDING: '#f97316',
      ADVANCE_PAID: '#22c55e',
      WORK_STARTED: '#3b82f6',
      COMPLETED: '#10b981',
      CLOSED: '#64748b',
    };
    return statusColors[status] || '#757575';
  };

  // Permission checks
  const canAssignPM = user?.role === 'admin' && project?.status === 'CREATED';
  const canAssignSite = user?.role === 'project_manager' && project?.pm_id === user.id && !project?.site_id;
  const canAssignFinance = user?.role === 'admin' && !project?.finance_id;
  const canStartWork = user?.role === 'project_manager' && project?.pm_id === user.id && project?.status === 'ADVANCE_PAID';
  const canMarkCompleted = user?.role === 'project_manager' && project?.pm_id === user.id && project?.status === 'WORK_STARTED';
  const canCloseProject = (user?.role === 'admin' || user?.role === 'super_admin') && project?.status === 'FINAL_PAID';
  const canAddExtraCharge = user?.role === 'finance' && ['CUSTOMER_APPROVED', 'ADVANCE_PENDING', 'ADVANCE_PAID', 'WORK_STARTED', 'COMPLETED'].includes(project?.status);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page-container">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>Project not found</p>
          <button className="btn-primary" onClick={() => navigate('/projects')}>
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Modern Project Header */}
<div className="project-hero">
  <div className="project-hero-left">
    <button className="btn-back-modern" onClick={() => navigate('/projects')}>
      <i className="fas fa-arrow-left"></i>
    </button>

    <div>
      <h1 className="project-title">{project.name}</h1>
      <p className="project-location">
        <i className="fas fa-map-marker-alt"></i> {project.location}
      </p>
    </div>
  </div>

  <div 
    className="project-status-badge"
    style={{ backgroundColor: getStatusColor(project.status) }}
  >
    {project.status.replace(/_/g, ' ')}
  </div>
</div>
<div className="status-stepper">
  {[
    "CREATED",
    "PM_ASSIGNED",
    "REPORT_SUBMITTED",
    "CUSTOMER_APPROVED",
    "ADVANCE_PAID",
    "WORK_STARTED",
    "COMPLETED",
    "CLOSED"
  ].map((step, index) => (
    <div 
      key={index}
      className={`step ${project.status === step ? "active" : ""}`}
    >
      <div className="step-dot"></div>
      <span>{step.replace(/_/g, ' ')}</span>
    </div>
  ))}
</div>
      {/* Action Buttons */}
      <div className="action-buttons-container">
        {canAssignPM && (
          <button 
            className="btn-action btn-primary"
            onClick={() => {
              setShowPMModal(true);
              fetchProjectManagers();
            }}
          >
            <i className="fas fa-user-tie"></i>
            Assign Project Manager
          </button>
        )}

        {canAssignSite && (
          <button 
            className="btn-action btn-primary"
            onClick={() => {
              setShowSiteModal(true);
              fetchSiteIncharges();
            }}
          >
            <i className="fas fa-hard-hat"></i>
            Assign Site Incharge
          </button>
        )}

        {canAssignFinance && (
          <button 
            className="btn-action btn-primary"
            onClick={() => {
              setShowFinanceModal(true);
              fetchFinanceUsers();
            }}
          >
            <i className="fas fa-dollar-sign"></i>
            Assign Finance Manager
          </button>
        )}

        {canStartWork && (
          <button 
            className="btn-action btn-success"
            onClick={() => handleUpdateStatus('WORK_STARTED', 'Are you sure you want to start work on this project?')}
          >
            <i className="fas fa-play"></i>
            Start Work
          </button>
        )}

        {canMarkCompleted && (
          <button 
            className="btn-action btn-success"
            onClick={() => handleUpdateStatus('COMPLETED', 'Are you sure the work has been completed?')}
          >
            <i className="fas fa-check-circle"></i>
            Mark as Completed
          </button>
        )}

        {canCloseProject && (
          <button 
            className="btn-action btn-danger"
            onClick={() => handleUpdateStatus('CLOSED', 'Are you sure you want to close this project?')}
          >
            <i className="fas fa-times-circle"></i>
            Close Project
          </button>
        )}

        {canAddExtraCharge && (
          <button 
            className="btn-action btn-warning"
            onClick={() => navigate(`/projects/${id}/add-extra-charge`, { state: { project } })}
          >
            <i className="fas fa-plus-circle"></i>
            Add Extra Charge
          </button>
        )}
      </div>

      {/* Project Details */}
      <div className="details-grid">
        <div className="details-card">
          <h3 className="card-title">Project Information</h3>
          <div className="detail-row">
            <span className="detail-label">Project ID</span>
            <span className="detail-value">{project.id}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Project Name</span>
            <span className="detail-value">{project.name}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Location</span>
            <span className="detail-value">{project.location}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Status</span>
            <span className="detail-value">{project.status.replace(/_/g, ' ')}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Created Date</span>
            <span className="detail-value">
              {new Date(project.created_at).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="details-card">
          <h3 className="card-title">Team Members</h3>
          <div className="detail-row">
            <span className="detail-label">Customer</span>
            <span className="detail-value">{project.customer_name || 'Not assigned'}</span>
          </div>
          {project.customer_email && (
            <div className="detail-row">
              <span className="detail-label">Customer Email</span>
              <span className="detail-value">{project.customer_email}</span>
            </div>
          )}
          {project.customer_phone && (
            <div className="detail-row">
              <span className="detail-label">Customer Phone</span>
              <span className="detail-value">{project.customer_phone}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Project Manager</span>
            <span className="detail-value">{project.pm_name || 'Not assigned'}</span>
          </div>
          {project.pm_email && (
            <div className="detail-row">
              <span className="detail-label">PM Email</span>
              <span className="detail-value">{project.pm_email}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Site Incharge</span>
            <span className="detail-value">{project.site_name || 'Not assigned'}</span>
          </div>
          {project.site_email && (
            <div className="detail-row">
              <span className="detail-label">Site Email</span>
              <span className="detail-value">{project.site_email}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Finance Manager</span>
            <span className="detail-value">{project.finance_name || 'Not assigned'}</span>
          </div>
          {project.finance_email && (
            <div className="detail-row">
              <span className="detail-label">Finance Email</span>
              <span className="detail-value">{project.finance_email}</span>
            </div>
          )}
        </div>

        {/* Payment Summary */}
        {paymentSummary && (
          <div className="details-card">
            <h3 className="card-title">Payment Summary</h3>
            <div className="detail-row">
              <span className="detail-label">Total Cost</span>
              <span className="detail-value">₹{paymentSummary.totalCost.toFixed(2)}</span>
            </div>
            {paymentSummary.extraChargesTotal > 0 && (
              <div className="detail-row">
                <span className="detail-label">Extra Charges</span>
                <span className="detail-value">₹{paymentSummary.extraChargesTotal.toFixed(2)}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Total with Extras</span>
              <span className="detail-value">₹{paymentSummary.totalCostWithExtras.toFixed(2)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Total Paid</span>
              <span className="detail-value text-success">₹{paymentSummary.totalPaid.toFixed(2)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Balance</span>
              <span className={`detail-value ${paymentSummary.balance > 0 ? 'text-danger' : 'text-success'}`}>
                ₹{paymentSummary.balance.toFixed(2)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Worklogs */}
      {worklogs.length > 0 && (
        <div className="worklogs-section">
          <h3 className="section-title">Work Logs</h3>
          <div className="worklogs-grid">
            {worklogs.map((worklog) => (
              <div key={worklog.id} className="worklog-card">
                <div className="worklog-header">
                  <span className="worklog-floor">Floor {worklog.floor_number}</span>
                  <span className="worklog-date">
                    {new Date(worklog.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="worklog-description">{worklog.description}</p>
                {worklog.images && worklog.images.length > 0 && (
                  <div className="worklog-images">
                    {worklog.images.map((img, idx) => {
                      // Images are stored as paths like "/uploads/filename.jpeg"
                      const imageUrl = `http://localhost:5000${img}`;
                      
                      return (
                        <img 
                          key={idx} 
                          src={imageUrl}
                          alt={`Floor ${worklog.floor_number} - Image ${idx + 1}`}
                          className="worklog-image"
                          onError={(e) => {
                            console.error('Failed to load image:', imageUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assign PM Modal */}
      {showPMModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowPMModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Project Manager</h2>
              <button className="modal-close" onClick={() => !submitting && setShowPMModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {loadingUsers ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading project managers...</p>
                </div>
              ) : projectManagers.length === 0 ? (
                <p className="empty-message">No project managers available</p>
              ) : (
                <div className="user-list">
                  {projectManagers.map((pm) => (
                    <div key={pm.id} className="user-item">
                      <div className="user-info">
                        <div className="user-name">{pm.name}</div>
                        <div className="user-email">{pm.email}</div>
                        <div className="user-meta">{pm.active_projects || 0} active projects</div>
                      </div>
                      <button
                        className="btn-select"
                        onClick={() => handleAssignPM(pm.id)}
                        disabled={submitting}
                      >
                        {submitting ? 'Assigning...' : 'Select'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assign Site Modal */}
      {showSiteModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowSiteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Site Incharge</h2>
              <button className="modal-close" onClick={() => !submitting && setShowSiteModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {loadingUsers ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading site incharges...</p>
                </div>
              ) : siteIncharges.length === 0 ? (
                <p className="empty-message">No site incharges available</p>
              ) : (
                <div className="user-list">
                  {siteIncharges.map((site) => (
                    <div key={site.id} className="user-item">
                      <div className="user-info">
                        <div className="user-name">{site.name}</div>
                        <div className="user-email">{site.email}</div>
                        <div className="user-meta">{site.active_projects || 0} active projects</div>
                      </div>
                      <button
                        className="btn-select"
                        onClick={() => handleAssignSite(site.id)}
                        disabled={submitting}
                      >
                        {submitting ? 'Assigning...' : 'Select'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assign Finance Modal */}
      {showFinanceModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowFinanceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Finance Manager</h2>
              <button className="modal-close" onClick={() => !submitting && setShowFinanceModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {loadingUsers ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading finance managers...</p>
                </div>
              ) : financeUsers.length === 0 ? (
                <p className="empty-message">No finance managers available</p>
              ) : (
                <div className="user-list">
                  {financeUsers.map((finance) => (
                    <div key={finance.id} className="user-item">
                      <div className="user-info">
                        <div className="user-name">{finance.name}</div>
                        <div className="user-email">{finance.email}</div>
                      </div>
                      <button
                        className="btn-select"
                        onClick={() => handleAssignFinance(finance.id)}
                        disabled={submitting}
                      >
                        {submitting ? 'Assigning...' : 'Select'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
